/**
 * BudgetFormModal - Create/Edit budget form modal
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BankAccount,
  AccountingStatementSummary,
  BudgetSummary,
  Budget,
  CreateBudgetRequest,
  UpdateBudgetRequest,
  BudgetSourceType,
  InflationScope,
} from '@budgira/shared';

interface BudgetFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateBudgetRequest) => Promise<void>;
  onUpdate?: (data: UpdateBudgetRequest) => Promise<void>;
  budget?: Budget | null;
  accounts: BankAccount[];
  statements?: AccountingStatementSummary[];
  existingBudgets?: BudgetSummary[];
  isSubmitting: boolean;
}

export const BudgetFormModal = ({
  isOpen,
  onClose,
  onSubmit,
  onUpdate,
  budget,
  accounts,
  statements = [],
  existingBudgets = [],
  isSubmitting,
}: BudgetFormModalProps) => {
  const { t } = useTranslation();

  const isEditMode = !!budget;

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);
  const [sourceType, setSourceType] = useState<BudgetSourceType>('manual');
  const [sourceId, setSourceId] = useState<string>('');
  const [applyInflation, setApplyInflation] = useState(false);
  const [inflationRate, setInflationRate] = useState<number>(3);
  const [inflationScope, setInflationScope] = useState<InflationScope>('all');
  const [changeReason, setChangeReason] = useState('');

  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize form when modal opens or budget changes
  useEffect(() => {
    if (isOpen) {
      if (budget) {
        // Edit mode - populate with existing data
        setName(budget.name);
        setDescription(budget.description || '');
        setStartDate(budget.startDate);
        setEndDate(budget.endDate);
        setSelectedAccountIds(budget.accounts.map((a) => a.accountId));
        setSourceType(budget.sourceType || 'manual');
        setSourceId(budget.sourceId || '');
        setApplyInflation(!!budget.inflationRate && budget.inflationRate > 0);
        setInflationRate(budget.inflationRate || 3);
        setInflationScope(budget.inflationScope || 'all');
        setChangeReason('');
      } else {
        // Create mode - reset form
        setName('');
        setDescription('');
        setStartDate('');
        setEndDate('');
        setSelectedAccountIds([]);
        setSourceType('manual');
        setSourceId('');
        setApplyInflation(false);
        setInflationRate(3);
        setInflationScope('all');
        setChangeReason('');
      }
      setErrors({});
    }
  }, [isOpen, budget]);

  const handleAccountToggle = (accountId: string) => {
    setSelectedAccountIds((prev) =>
      prev.includes(accountId)
        ? prev.filter((id) => id !== accountId)
        : [...prev, accountId]
    );
  };

  const selectAllAccounts = () => {
    setSelectedAccountIds(accounts.map((a) => a.id));
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = t('budgets.errors.nameRequired');
    }
    if (!startDate) {
      newErrors.startDate = t('budgets.errors.startDateRequired');
    }
    if (!endDate) {
      newErrors.endDate = t('budgets.errors.endDateRequired');
    }
    if (startDate && endDate && startDate > endDate) {
      newErrors.endDate = t('budgets.errors.endDateBeforeStart');
    }
    if (selectedAccountIds.length === 0) {
      newErrors.accounts = t('budgets.errors.accountRequired');
    }
    if (!isEditMode && (sourceType === 'statement' || sourceType === 'budget') && !sourceId) {
      newErrors.sourceId = t('budgets.errors.validation');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    if (isEditMode && onUpdate) {
      // Update mode
      const data: UpdateBudgetRequest = {
        name: name.trim(),
        description: description.trim() || null,
        startDate,
        endDate,
        accountIds: selectedAccountIds,
        changeReason: changeReason.trim() || undefined,
      };
      await onUpdate(data);
    } else {
      // Create mode
      const data: CreateBudgetRequest = {
        name: name.trim(),
        description: description.trim() || undefined,
        startDate,
        endDate,
        accountIds: selectedAccountIds,
        sourceType: sourceType === 'manual' ? undefined : sourceType,
        sourceId: sourceType === 'manual' ? undefined : sourceId,
        inflationRate: applyInflation && inflationRate > 0 ? inflationRate : undefined,
        inflationScope: applyInflation ? inflationScope : undefined,
      };
      await onSubmit(data);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl p-6 max-w-2xl w-full mx-4 my-8 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-6">
          {isEditMode ? t('budgets.editTitle') : t('budgets.createTitle')}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {t('budgets.name')} *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('budgets.namePlaceholder')}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
            />
            {errors.name && <p className="text-sm text-red-600 mt-1">{errors.name}</p>}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {t('budgets.description')}
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('budgets.descriptionPlaceholder')}
              rows={2}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
            />
          </div>

          {/* Period */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t('budgets.startDate')} *
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
              />
              {errors.startDate && (
                <p className="text-sm text-red-600 mt-1">{errors.startDate}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t('budgets.endDate')} *
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
              />
              {errors.endDate && (
                <p className="text-sm text-red-600 mt-1">{errors.endDate}</p>
              )}
            </div>
          </div>

          {/* Source type - only for creation */}
          {!isEditMode && (
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                {t('budgets.source')}
              </label>
              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="sourceType"
                    checked={sourceType === 'manual'}
                    onChange={() => {
                      setSourceType('manual');
                      setSourceId('');
                    }}
                    className="text-violet-600"
                  />
                  <span className="text-sm text-slate-700 dark:text-slate-300">
                    {t('budgets.sourceManual')}
                  </span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="sourceType"
                    checked={sourceType === 'budget'}
                    onChange={() => setSourceType('budget')}
                    className="text-violet-600"
                    disabled={existingBudgets.length === 0}
                  />
                  <span className="text-sm text-slate-700 dark:text-slate-300">
                    {t('budgets.sourceBudget')}
                  </span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="sourceType"
                    checked={sourceType === 'statement'}
                    onChange={() => setSourceType('statement')}
                    className="text-violet-600"
                    disabled={statements.length === 0}
                  />
                  <span className="text-sm text-slate-700 dark:text-slate-300">
                    {t('budgets.sourceStatement')}
                  </span>
                </label>
              </div>
            </div>
          )}

          {/* Source selection - only for creation */}
          {!isEditMode && sourceType === 'budget' && existingBudgets.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t('budgets.selectBudget')} *
              </label>
              <select
                value={sourceId}
                onChange={(e) => setSourceId(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
              >
                <option value="">-- {t('budgets.selectSource')} --</option>
                {existingBudgets.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name} ({new Date(b.startDate).getFullYear()})
                  </option>
                ))}
              </select>
              {errors.sourceId && (
                <p className="text-sm text-red-600 mt-1">{errors.sourceId}</p>
              )}
            </div>
          )}

          {!isEditMode && sourceType === 'statement' && statements.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t('budgets.selectStatement')} *
              </label>
              <select
                value={sourceId}
                onChange={(e) => setSourceId(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
              >
                <option value="">-- {t('budgets.selectSource')} --</option>
                {statements.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} ({new Date(s.startDate).getFullYear()})
                  </option>
                ))}
              </select>
              {errors.sourceId && (
                <p className="text-sm text-red-600 mt-1">{errors.sourceId}</p>
              )}
            </div>
          )}

          {/* Inflation - only for creation when source is selected */}
          {!isEditMode && (sourceType === 'budget' || sourceType === 'statement') && sourceId && (
            <div className="p-4 bg-slate-50 dark:bg-slate-900/50 rounded-lg space-y-3">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={applyInflation}
                  onChange={(e) => setApplyInflation(e.target.checked)}
                  className="text-violet-600 rounded"
                />
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  {t('budgets.applyInflation')}
                </span>
              </label>

              {applyInflation && (
                <>
                  <div>
                    <label className="block text-sm text-slate-600 dark:text-slate-400 mb-1">
                      {t('budgets.inflationRate')} (%)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={inflationRate}
                      onChange={(e) => setInflationRate(parseFloat(e.target.value) || 0)}
                      className="w-24 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-600 dark:text-slate-400 mb-1">
                      {t('budgets.inflationScope')}
                    </label>
                    <select
                      value={inflationScope}
                      onChange={(e) => setInflationScope(e.target.value as InflationScope)}
                      className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                    >
                      <option value="all">{t('budgets.inflationAll')}</option>
                      <option value="income">{t('budgets.inflationIncome')}</option>
                      <option value="expenses">{t('budgets.inflationExpenses')}</option>
                    </select>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Accounts */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                {t('budgets.accounts')} *
              </label>
              <button
                type="button"
                onClick={selectAllAccounts}
                className="text-xs text-violet-600 hover:text-violet-700"
              >
                {t('common.viewAll')}
              </button>
            </div>
            <div className="max-h-40 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded-lg divide-y divide-slate-200 dark:divide-slate-700">
              {accounts.length === 0 ? (
                <p className="p-3 text-sm text-slate-500 dark:text-slate-400">
                  {t('budgets.noAccounts')}
                </p>
              ) : (
                accounts.map((account) => (
                  <label
                    key={account.id}
                    className="flex items-center gap-3 p-3 hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedAccountIds.includes(account.id)}
                      onChange={() => handleAccountToggle(account.id)}
                      className="text-violet-600 rounded"
                    />
                    <span className="text-sm text-slate-900 dark:text-slate-100">
                      {account.name}
                    </span>
                  </label>
                ))
              )}
            </div>
            {errors.accounts && (
              <p className="text-sm text-red-600 mt-1">{errors.accounts}</p>
            )}
          </div>

          {/* Change reason - only for edit mode */}
          {isEditMode && (
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t('budgets.changeReason')}
              </label>
              <input
                type="text"
                value={changeReason}
                onChange={(e) => setChangeReason(e.target.value)}
                placeholder={t('budgets.changeReason')}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
              />
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 rounded-lg disabled:opacity-50"
            >
              {isSubmitting ? t('common.saving') : isEditMode ? t('common.save') : t('common.create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BudgetFormModal;
